/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionRequest,
  ActionSuccess,
  AssetUtils,
  Channel,
  Genres,
  Minutes,
  Program,
  TimeUtils,
  Ducks,
  Recommendations,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { Selectors as ChannelsSelectors } from 'core/store/ducks/epg/channels';
import { StoreState } from '../../store';
import { ChannelFilter, CHANNEL_FILTERS } from './channelFilters';
import { getTopChannels } from '../../../core/store/ducks/epg/topChannels';

// ACTIONS
export enum ActionType {
  FILTER_CHANNELS = 'watchTv/FILTER_CHANNELS',
  FILTER_CHANNELS_SUCCESS = 'watchTv/FILTER_CHANNELS_SUCCESS',
}

type FilterChannels = ActionRequest<ActionType.FILTER_CHANNELS, ChannelFilter>;
type FilterChannelsSuccess = ActionSuccess<ActionType.FILTER_CHANNELS_SUCCESS, Channel[], string>;

type Actions = FilterChannels | FilterChannelsSuccess;

const Actions = {
  filterChannels: (filter: ChannelFilter): FilterChannels => ({ type: ActionType.FILTER_CHANNELS, meta: filter }),
  filterChannelsSuccess: (channels: Channel[], meta: string): FilterChannelsSuccess => ({
    type: ActionType.FILTER_CHANNELS_SUCCESS,
    payload: channels,
    meta,
  }),
};

export const ActionCreators = {
  filterChannels: Actions.filterChannels,
};

//
// REDUCER
//

type ReducerState = {
  filter?: string;
  channels: Channel[];
  processing: boolean;
};

const initialState: ReducerState = {
  channels: [],
  processing: false,
};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.FILTER_CHANNELS:
      return {
        ...state,
        processing: true,
      };
    case ActionType.FILTER_CHANNELS_SUCCESS:
      return {
        filter: action.meta,
        channels: action.payload,
        processing: false,
      };

    default:
      return state;
  }
}

//
// SAGAS
//

// private sagas

function isChannelDisabled(channel: Channel): boolean {
  return channel.streams.length === 0; // TODO: disables channels are the ones without a HD or SD stream
}

/**
 * Shows all channels that includes:
 * 1. DTT Only channels
 * 2. If converged mode is active all OTT channels, if not only OTT subscribed channels (this is done in the lib)
 * 3. Disabled Channels
 */
function allChannels(channels: Channel[]): Channel[] {
  return channels;
}

/**
 * Show N top channels from recommendations
 * fallback 1: kaltura top channels
 * fallback 2: first channels of the channel list
 */
function* isMostWatched(channels: Channel[]): SagaIterator<Channel[]> {
  const topChannels: Channel[] | undefined = yield call(getTopChannels);
  const mostWatched: Channel[] = topChannels?.length ? topChannels : channels;
  const filteredChannels = allChannels(mostWatched); // apply filters from all channels, if any
  const configs: Recommendations = yield select(Ducks.Dms.Selectors.getRecommendations);

  return filteredChannels.slice(0, configs.topChannelsMaxAssets);
}

/**
 * Shows web channels (only OTT subscribed channels or disabled channels)
 */
function isOTT(channels: Channel[]): Channel[] {
  return channels.filter(
    (channel) => (AssetUtils.isOTT(channel) && channel.isSubscribed) || isChannelDisabled(channel)
  );
}

/**
 * Channels with programs started in the last minutes
 */
function* isJustStarted(channels: Channel[]): SagaIterator<Channel[]> {
  const filteredChannels = allChannels(channels); // apply filters from all channels, if any
  const epgs = filteredChannels.map((channel) => channel.epgId);
  const justStartedChannels: Channel[] = [];
  const programs: Program[] = yield call(Ducks.Epg.Live.getLivePrograms, epgs);

  for (let i = 0; i < epgs.length; i += 1) {
    const program: Program | undefined = programs.find((prog) => prog.channelEpgId === epgs[i]);
    if (program) {
      const duration: Minutes = program.durationValue;

      // Calculate threshold according to TF28.006.01
      let threshold: Minutes = 0;
      if (duration <= 45) {
        threshold = 5;
      } else if (duration <= 90) {
        threshold = 10;
      } else {
        threshold = 15;
      }

      if (TimeUtils.isAfterNow(TimeUtils.addMinutes(program.startDate, threshold))) {
        justStartedChannels.push(filteredChannels[i]);
      }
    }
  }

  return justStartedChannels;
}

function isHd(channels: Channel[]): Channel[] {
  return channels.filter((channel) => !channel.genres.includes(Genres.HD));
}

function isSubscribed(channels: Channel[]): Channel[] {
  return channels.filter((channel) => !!channel.isSubscribed);
}

function hasSelectedGenres(channels: Channel[], genreName: string): Channel[] {
  return channels.filter((channel) => channel.genres.includes(genreName));
}

function* filterChannels(action: FilterChannels): SagaIterator {
  const { meta } = action;
  const channels: Channel[] | undefined = yield select(ChannelsSelectors.getChannels);
  let filteredChannels: Channel[] = [];
  if (channels?.length) {
    switch (meta.id) {
      case CHANNEL_FILTERS.mostWatched:
        filteredChannels = yield call(isMostWatched, channels);
        break;
      case CHANNEL_FILTERS.allChannels:
        filteredChannels = allChannels(channels);
        break;
      case CHANNEL_FILTERS.OTTFilter:
        filteredChannels = isOTT(channels);
        break;
      case CHANNEL_FILTERS.justStarted:
        filteredChannels = yield call(isJustStarted, channels);
        break;
      case CHANNEL_FILTERS.hdChannels:
        filteredChannels = isHd(channels);
        break;
      case CHANNEL_FILTERS.subscribed:
        filteredChannels = isSubscribed(channels);
        break;
      default:
        filteredChannels = hasSelectedGenres(channels, meta.name);
        break;
    }
  }
  yield put(Actions.filterChannelsSuccess(filteredChannels, meta.id.toString()));
}

// public sagas
export function* saga(): SagaIterator {
  yield all([takeLatest(ActionType.FILTER_CHANNELS, filterChannels)]);
}

//
// SELECTORS
//

export const Selectors = {
  getFilteredChannels: (state: StoreState): Channel[] => state.watchTv.filterChannels?.channels,
  isChannelFilterProcessing: (state: StoreState): boolean => state.watchTv.filterChannels?.processing,
};
